import * as Api from '@ViewModels';
import moment from 'moment';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { hasLegalV2IntegrationEnabled } from '../../../../../models/utils/integrationUtils';
import { useGetExternalIntegrationsQuery, useLegalMatterFilter } from '../../../../../queries';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../../components-v2/Dropdown';
import { Pill } from '../../../../components-v2/Pill';
import { THEME } from '../../../../components-v2/Theme/theme';
import { EditMatterModal } from '../../../dataBoards/matters/EditMatterModal/';
import { FolderIcon } from '../../../svgs/icons/FolderIcon';
import { DataBoardRow, DataBoardTable } from '../../DataBoardTable';
import { DeleteModal, DeleteType } from '../../DeleteModal';

const formatDate = (dateString?: string) => {
	if (!dateString) return '-';
	return moment(dateString).format('MM/DD/YYYY');
};

export const EntityMatters = ({ entity }: { entity: Api.IEntity }) => {
	const userSession = useUserSession();
	// Check both v1 and v2 integrations for legal providers
	const { data: externalIntegrations } = useGetExternalIntegrationsQuery({ impersonationContext: undefined });
	const legalV2IntegrationEnabled = hasLegalV2IntegrationEnabled(externalIntegrations);
	const canEdit = !userSession.hasLegalIntegrationConfigured && !legalV2IntegrationEnabled;

	const [matterToEdit, setMatterToEdit] = React.useState<Partial<Api.ILegalMatter> | null>(null);
	const [matterToDelete, setMatterToDelete] = React.useState<Api.ILegalMatter | null>(null);
	const [refreshToken, setRefreshToken] = React.useState(new Date().getTime());
	const matterFilterQuery = useLegalMatterFilter({
		enabled: !!entity.id,
		filterRequest: {
			criteria: [
				{
					property: 'contactId',
					value: entity.id ?? '',
				},
			],
		},
	});

	const matters = React.useMemo(() => {
		return matterFilterQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [matterFilterQuery.data]);

	const onFetchNextBatchOfMatters = () => {
		if (matterFilterQuery.hasNextPage && !matterFilterQuery.isFetchingNextPage) {
			matterFilterQuery.fetchNextPage();
		}
	};

	const refreshMatters = () => {
		setRefreshToken(new Date().getTime());
		setMatterToEdit(null);
		setMatterToDelete(null);
	};

	const newMatter: Partial<Api.ILegalMatter> = {
		contact: entity as Api.IProjectedContact,
		contactId: entity.id,
	};

	const openCount = matters.filter(matter => !matter.closeDate).length;
	const summary =
		matters.length && !matterFilterQuery.hasNextPage
			? `${matters.length} ${matters.length === 1 ? 'matter' : 'matters'} · ${openCount} open`
			: undefined;

	const rows: DataBoardRow[] = matters.map(matter => ({
		id: matter.id,
		name: matter.matterName,
		statusTitle: matter.stage?.name,
		status: matter.stage?.name ? <Pill variant='label' color='blue' label={matter.stage.name} /> : '-',
		fields: [matter.matterNumber || '-', formatDate(matter.openDate), formatDate(matter.closeDate)],
		actions:
			canEdit && matter.canModify ? (
				<Dropdown>
					<DropdownEllipsisTrigger aria-label={`Actions for matter ${matter.matterName ?? ''}`.trim()} />
					<DropdownContent>
						<DropdownItem onClick={() => setMatterToEdit(matter)}>Edit</DropdownItem>
						<DropdownItem onClick={() => setMatterToDelete(matter)}>Delete</DropdownItem>
					</DropdownContent>
				</Dropdown>
			) : undefined,
	}));

	return (
		<div key={refreshToken}>
			<DataBoardTable
				icon={<FolderIcon fillColor={THEME.colors.primary.contrastText} />}
				iconColor='green'
				title='Matters'
				summary={summary}
				addItemName='Matter'
				addDisabledReason={
					canEdit ? undefined : 'Matters sync from your legal practice integration, so add them there.'
				}
				onAdd={() => setMatterToEdit(newMatter)}
				nameLabel='Matter'
				statusLabel='Stage'
				fieldLabels={['Matter #', 'Opened', 'Closed']}
				rows={rows}
				isLoading={matterFilterQuery.isPending}
				emptyText={
					matterFilterQuery.isError ? 'Couldn’t load this contact’s matters.' : 'No matters exist for this contact.'
				}
			>
				{matters.length ? <Waypoint bottomOffset='-200px' onEnter={onFetchNextBatchOfMatters} /> : null}
			</DataBoardTable>

			{matterToEdit ? (
				<EditMatterModal
					initialMatter={matterToEdit}
					isOpen={!!matterToEdit}
					setIsOpen={isOpen => {
						if (!isOpen) {
							setMatterToEdit(null);
						}
					}}
					onSave={refreshMatters}
					onCancel={() => setMatterToEdit(null)}
				/>
			) : null}

			{matterToDelete ? (
				<DeleteModal
					type={DeleteType.Matter}
					item={matterToDelete}
					isOpen={!!matterToDelete}
					setIsOpen={isOpen => {
						if (!isOpen) {
							setMatterToDelete(null);
						}
					}}
					onConfirm={refreshMatters}
				/>
			) : null}
		</div>
	);
};
